import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useGlobalSnackbar } from "../../../../context/SnackbarContext";
import { useAppSelector } from "../../../../redux/hooks/redux";
import { useVendors } from "../../../vendors/hooks/useVendors";
import { VendorsTable } from "../../../vendors/vendorsTable";
import { invalidateContactResolutionQueries, performSmartContactCleanup } from "../hooks/useContacts";

type Props = {
  phoneNumber: string;
  onDone: () => void;
  initialActiveTable?: "vendors" | "prospects";
};

export function LinkVendorPickerContent({ phoneNumber, onDone, initialActiveTable = "vendors" }: Props) {
  const { editVendor } = useVendors();
  const { showSnackbar } = useGlobalSnackbar();
  const queryClient = useQueryClient();
  const companyId = useAppSelector((state) => state.userState.company?.id);
  const [isLinking, setIsLinking] = useState(false);

  const contact = {
    phoneNumbers: [{ number: phoneNumber, label: "primary", isPrimary: true }],
  };

  const handleLinkVendor = async (vendorData: any) => {
    if (isLinking) return;
    setIsLinking(true);

    const { vendorUid, phoneNumbers: existingPhones } = vendorData;

    try {
      const digits = phoneNumber.replace(/\D/g, "");
      let dialCode = "+1";
      let cleanPhone = digits;
      if (digits.startsWith("1") && digits.length === 11) {
        cleanPhone = digits.slice(1);
      }

      const newPhoneObj = {
        countryCode: "US",
        dialCode,
        phone: cleanPhone,
        phoneNumber: dialCode + cleanPhone,
        tag: "other",
        isDefault: false,
      };

      const phones = Array.isArray(existingPhones) ? existingPhones : [];

      const alreadyHasPhone = phones.some((p: any) => p.phone === cleanPhone || p.phoneNumber === dialCode + cleanPhone);

      const combinedPhones = alreadyHasPhone ? phones : [...phones, newPhoneObj];

      const res = await editVendor(vendorUid, {
        phoneNumbers: combinedPhones,
      });

      if (res?.ok) {
        await performSmartContactCleanup(phoneNumber, companyId, queryClient);
        await invalidateContactResolutionQueries(queryClient);
        showSnackbar("Phone number linked to vendor successfully", "success");
        onDone();
      } else {
        showSnackbar("Failed to link phone number to vendor", "error");
      }
    } catch (error: any) {
      showSnackbar(error?.message || "Failed to link phone number to vendor", "error");
    } finally {
      setIsLinking(false);
    }
  };

  return <VendorsTable contact={contact} onLinkContact={handleLinkVendor} initialPhoneNumber={phoneNumber} initialActiveTable={initialActiveTable} />;
}
